import {
  useCallback,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import cs from 'clsx';
import classes from './floating-row-indicators.module.css';

export type FloatingRowIndicatorVariant = 'hover' | 'active';

export type FloatingRowIndicatorRender = (payload: {
  variant: FloatingRowIndicatorVariant;
}) => ReactNode;

export type FloatingRowIndicatorsProps = {
  parent: HTMLElement | null;
  hoverTarget: HTMLElement | null;
  activeTarget: HTMLElement | null;
  hoverClassName?: string;
  activeClassName?: string;
  hoverTransitionDuration?: number;
  activeTransitionDuration?: number;
  hoverTestId?: string;
  activeTestId?: string;
  children?: FloatingRowIndicatorRender;
};

type IndicatorBox = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const INDICATOR_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  zIndex: 0,
  willChange: 'transform',
  transitionProperty: 'transform, height',
  transitionTimingFunction: 'ease',
};

const measure = (target: HTMLElement, parent: HTMLElement): IndicatorBox => {
  const targetRect = target.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  return {
    top: Math.round(targetRect.top - parentRect.top),
    left: Math.round(targetRect.left - parentRect.left),
    width: Math.round(targetRect.width),
    height: Math.round(targetRect.height),
  };
};

const isSameBox = (left: IndicatorBox, right: IndicatorBox) =>
  Math.abs(left.top - right.top) <= 1 &&
  Math.abs(left.left - right.left) <= 1 &&
  left.width === right.width &&
  left.height === right.height;

const RowIndicator = ({
  target,
  parent,
  className,
  transitionDuration,
  testId,
  children,
  indicatorNodes,
}: {
  target: HTMLElement | null;
  parent: HTMLElement | null;
  className?: string;
  transitionDuration: number;
  testId: string;
  children?: ReactNode;
  indicatorNodes: RefObject<Set<HTMLElement>>;
}) => {
  const boxRef = useRef<IndicatorBox | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const registerIndicator = useCallback(
    (node: HTMLDivElement | null) => {
      if (nodeRef.current) indicatorNodes.current.delete(nodeRef.current);
      nodeRef.current = node;
      if (node) indicatorNodes.current.add(node);
    },
    [indicatorNodes],
  );

  useLayoutEffect(() => {
    if (!target || !parent) {
      boxRef.current = null;
      return;
    }

    let frame: number | undefined;
    const update = () => {
      frame = undefined;
      const node = nodeRef.current;
      if (!node) return;
      if (!target.isConnected || !parent.isConnected) {
        node.style.visibility = 'hidden';
        boxRef.current = null;
        return;
      }
      const next = measure(target, parent);
      if (boxRef.current && isSameBox(boxRef.current, next)) return;
      boxRef.current = next;
      node.style.width = `${next.width}px`;
      node.style.height = `${next.height}px`;
      node.style.transform = `translate3d(${next.left}px, ${next.top}px, 0)`;
      node.style.visibility = 'visible';
    };
    const scheduleUpdate = () => {
      if (frame === undefined) frame = requestAnimationFrame(update);
    };

    update();
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(parent);
    resizeObserver.observe(target);
    const mutationObserver = new MutationObserver((records) => {
      // Ignore our own animated styles to avoid measurement feedback loops.
      if (
        records.some(
          (record) =>
            !Array.from(indicatorNodes.current).some((node) =>
              node.contains(record.target),
            ),
        )
      )
        scheduleUpdate();
    });
    mutationObserver.observe(parent, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
      characterData: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [parent, target, indicatorNodes]);

  if (!target || !parent) return null;

  return (
    <div
      ref={registerIndicator}
      className={cs(classes.indicator, className)}
      data-testid={testId}
      style={{
        ...INDICATOR_STYLE,
        transitionDuration: `${transitionDuration}ms`,
      }}
    >
      {children}
    </div>
  );
};

export const FloatingRowIndicators = ({
  parent,
  hoverTarget,
  activeTarget,
  hoverClassName,
  activeClassName,
  hoverTransitionDuration = 0,
  activeTransitionDuration = 0,
  hoverTestId = 'floating-row-hover-indicator',
  activeTestId = 'floating-row-active-indicator',
  children,
}: FloatingRowIndicatorsProps) => {
  const indicatorNodes = useRef(new Set<HTMLElement>());
  return (
    <>
      {children || hoverClassName ? (
        <RowIndicator
          indicatorNodes={indicatorNodes}
          target={hoverTarget}
          parent={parent}
          className={hoverClassName}
          transitionDuration={hoverTransitionDuration}
          testId={hoverTestId}
        >
          {children?.({ variant: 'hover' })}
        </RowIndicator>
      ) : null}
      {children || activeClassName ? (
        <RowIndicator
          indicatorNodes={indicatorNodes}
          target={activeTarget}
          parent={parent}
          className={activeClassName}
          transitionDuration={activeTransitionDuration}
          testId={activeTestId}
        >
          {children?.({ variant: 'active' })}
        </RowIndicator>
      ) : null}
    </>
  );
};